import { useReducer, useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { singletonHook } from 'react-singleton-hook'
import { useEffectOnce } from 'react-use'
import cloneDeep from 'lodash/cloneDeep'
import { IUserDetails, IUserPermissions, IShipmentPermission } from 'types'
import { getUserDetails as getApiUserDetails } from 'utils/api/getUserDetails'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { fmsGraphQLClient, marketplaceClient } from 'utils/graphql/client'
import { ILogoutProps, getUserCookieValues, logout, storeUserData } from 'utils/userAuth'

export interface IUseUserDetailsState {
  loading: boolean
  userDetails: IUserDetails | null
  userPermissions: IUserPermissions | null
  shipmentPermissions: IShipmentPermission[]
  authToken: string | null
  error: string | null
}

export interface IUserDetailsHook extends IUseUserDetailsState {
  updateUserDetails?: () => Promise<void>
  clearUserDetails?: () => void
  logout: (props?: ILogoutProps) => void
  userIsAuthenticated?: boolean
  isFPUser?: boolean
  isMarketplaceAdmin?: boolean
  isTMSUser?: boolean
}

const reducer = (
  state: IUseUserDetailsState,
  payload: Partial<IUseUserDetailsState>
): IUseUserDetailsState => ({
  ...state,
  ...payload,
})

const initialState = {
  loading: true,
  userDetails: null,
  userPermissions: null,
  shipmentPermissions: [],
  error: null,
  authToken: null,
}

// Holds the Singleton state that will be used to non React code
let globalState: IUseUserDetailsState = cloneDeep(initialState)

export const getUserDetails = () => Object.freeze(globalState.userDetails)
export const getUserPermissions = () => Object.freeze(globalState.userPermissions)
export const getUserShipmentPermissions = () => Object.freeze(globalState.shipmentPermissions)
export const getUserAuthToken = () => Object.freeze(globalState.authToken)

export const useUserDetailsHook = () => {
  const [state, setState] = useReducer(reducer, globalState)
  const history = useHistory()
  const { getFlag } = useFeatureFlagContext()

  const userIsAuthenticated = useMemo(() => !state.loading && !!state.authToken, [
    state.loading,
    state.authToken,
  ])

  const { redirectToFp, redirectToTms, userType } = state.userDetails || {}
  const isFPUser = userIsAuthenticated && redirectToFp
  const isTMSUser = userIsAuthenticated && redirectToTms
  const isMarketplaceAdmin = !isTMSUser && !isFPUser && userType === 'admin'

  const clearUserDetails = useCallback(() => {
    globalState = cloneDeep(initialState)
    setState(globalState)
  }, [])

  const logoutHandler = useCallback(
    (props: ILogoutProps = {}) => {
      const { enabled: rogersAuthPagesEnabled } = getFlag('rogersAuthPages')

      fmsGraphQLClient.clearStore()
      marketplaceClient.clearStore()

      if (rogersAuthPagesEnabled) {
        history.push('/signout', {
          state: { app: props.origin },
          from: history.location,
        })
      } else {
        logout(props)
      }
    },
    [getFlag, history]
  )

  const callGetUserDetails = useCallback(async () => {
    const { authToken, userId } = getUserCookieValues()

    // Retrieves the cookies values from staging and stores them as own cookies
    if (__ENV__ === 'development') {
      const { localLogin } = Object.fromEntries(new URLSearchParams(window.location.search))

      if (localLogin) {
        try {
          const localLoginObj = JSON.parse(localLogin)

          await storeUserData({
            ...localLoginObj,
            id: localLoginObj.userId,
          })

          window.location.href = __ROOT__

          return
        } catch (error) {
          window.console.warn('login info is malformed')
        }
      }
    }

    if (!userId) {
      globalState.loading = false

      setState(globalState)

      return
    }

    try {
      const { shipmentPermissions, user, userPermissions } = await getApiUserDetails(userId)

      globalState = {
        userDetails: user,
        userPermissions,
        shipmentPermissions,
        authToken,
        loading: false,
        error: null,
      }

      fmsGraphQLClient.clearStore()
      marketplaceClient.clearStore()

      setState(globalState)
    } catch (error) {
      globalState.loading = false
      globalState.error = 'There was an error getting User Details'

      setState(globalState)
    }
  }, [])

  useEffectOnce(() => {
    callGetUserDetails()
  })

  return {
    ...state,
    updateUserDetails: callGetUserDetails,
    clearUserDetails,
    logout: logoutHandler,
    userIsAuthenticated,
    isFPUser,
    isTMSUser,
    isMarketplaceAdmin,
  }
}

export const useUserDetails = singletonHook<IUserDetailsHook>(
  globalState as IUserDetailsHook,
  useUserDetailsHook
)

export default useUserDetails
